/**
 * WHAT A SIGNED-IN ACCOUNT WITH NO ORGANISATION SEES.
 *
 * Access to every study is decided by the organisation stamped on the
 * account's sign-in. On 2026-09-29 an Ubuntu Prod account created from the
 * admin Users page signed in without one. The dashboard said "not attached";
 * the study link said "Project not found — the project you're looking for
 * doesn't exist", which was false. Less than a minute later the account
 * accepted an invitation and gained its organisation, but the browser kept
 * the old sign-in until the person signed out.
 *
 * Two rules follow, and live here so they can be tested without a browser:
 *   1. Before telling someone they have no organisation, refresh their
 *      sign-in ONCE and look again (shouldRecheckOrg). That alone clears the
 *      case above.
 *   2. A project that cannot be loaded is described by WHY: no organisation,
 *      no access, or genuinely not found. Only the last may say "not found".
 *
 * Pure and DOM-free: `web` has no test runner and must not gain one.
 */

export interface ProjectLoadState {
  kind: 'ok' | 'not-found' | 'no-access' | 'no-organisation';
  title: string;
  body: string;
}

export const NO_ORGANISATION_TITLE = 'Your account is not attached to an organisation';
export const NO_ORGANISATION_BODY =
  'Studies are shared within an organisation, and this account does not belong to one yet, ' +
  'so it cannot open any study. Nothing has been lost. Ask your administrator to add your ' +
  'account to your organisation, then choose "Check again".';

export function projectLoadState(input: {
  exists: boolean;
  permissionDenied: boolean;
  hasOrg: boolean;
}): ProjectLoadState {
  if (input.exists) return { kind: 'ok', title: '', body: '' };
  if (!input.hasOrg) {
    return { kind: 'no-organisation', title: NO_ORGANISATION_TITLE, body: NO_ORGANISATION_BODY };
  }
  if (input.permissionDenied) {
    return {
      kind: 'no-access',
      title: 'You do not have access to this study',
      body: 'Ask the study owner to invite you, or check that you are signed in with the right account.',
    };
  }
  return {
    kind: 'not-found',
    title: 'Project not found',
    body: 'This study may have been deleted, or the link may be wrong.',
  };
}

/** Refresh the sign-in once, and only once, before reporting a missing organisation. */
export function shouldRecheckOrg(input: { orgMissing: boolean; alreadyRechecked: boolean }): boolean {
  return input.orgMissing && !input.alreadyRechecked;
}

export function isPermissionDenied(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && (code === 'permission-denied' || code.endsWith('/permission-denied'));
}
